/**
 * §H6 spi — "geography select (countries + regions in scope)"; result "Spark trajectory
 * (ochre), per-year table (value + dot + label), current-status callout box (border in
 * class colour, dekadal-refresh note)".
 *
 * **Sync**: the POST answers `{status:'done', result}` and there is no poll (T6). The
 * progress bar is still wired, because the frame does not know which tools are sync and
 * should not have to — it simply never gets a running phase here.
 *
 * The classes are the **server's**: `key` is the stable token and `label`/`color` come from
 * `spi.classify`, which §E8's export renderer also draws its swatches from. Restating the
 * thresholds here would be a second copy of the classification that could drift from the
 * one the PDF prints.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToolRun, type SpiResult } from '../../../lib/analysis';
import { fetchUnitOptions, queryKeys } from '../../../lib/api';
import { Spark } from '../../../components/charts';
import { NO_VALUE, fmtNum } from '../../../lib/format';
import { Icon } from '../../../lib/icons';
import { selectScopeParent, useAppStore } from '../../../lib/state';
import { toast } from '../../../lib/toast';
import type { ToolProps } from '../AnalysisDock';
import { ToolFrame } from '../ToolFrame';
import { ResultBox, ResultNote } from '../ResultBox';

/** §H6's trajectory colour — Part H's tile ochre. */
export const SPI_COLOR = '#C77B21';

export function Spi({ onBack }: ToolProps) {
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const parent = useAppStore((s) => selectScopeParent(s));

  const [unitId, setUnitId] = useState('');
  const run = useToolRun<SpiResult>('spi');

  // §H6's "countries + regions in scope" — two levels, one select, countries first.
  const { data: countries } = useQuery({
    queryKey: queryKeys.unitOptions('admin', 0, country, parent, false),
    queryFn: () => fetchUnitOptions('admin', 0, country, parent, false),
  });
  const { data: regions } = useQuery({
    queryKey: queryKeys.unitOptions('admin', 1, country, parent, false),
    queryFn: () => fetchUnitOptions('admin', 1, country, parent, false),
  });
  const options = useMemo(() => [...(countries ?? []), ...(regions ?? [])], [countries, regions]);
  const current = options.some((u) => u.id === unitId) ? unitId : (options[0]?.id ?? '');

  const submit = () => {
    if (!current) {
      toast(t('analysis.spi.noUnit'));
      return;
    }
    run.run({ unit_id: current });
  };

  const result = run.result;
  const classByYear = useMemo(
    () => new Map((result?.classification ?? []).map((c) => [c.year, c])),
    [result],
  );

  return (
    <ToolFrame
      tool="spi"
      onBack={onBack}
      phase={run.phase}
      progress={run.progress}
      error={run.error}
      cached={run.cached}
      result={
        result ? (
          <>
            <ResultBox title={t('analysis.spi.trajectory', { unit: result.unit.name })}>
              <div className="result-pad">
                <Spark
                  series={result.series.map((p) => p.spi3)}
                  labels={result.series.map((p) => p.year)}
                  w={320}
                  h={80}
                  color={SPI_COLOR}
                  unit={result.indicator.unit}
                  fmt={result.indicator.fmt}
                  ariaLabel={t('analysis.spi.trajectory', { unit: result.unit.name })}
                />
              </div>
              <table className="mini">
                <tbody>
                  {result.series.map((point) => {
                    const cls = classByYear.get(point.year);
                    return (
                      <tr key={point.year}>
                        <td>{point.year}</td>
                        <td className="num">
                          {point.spi3 === null ? NO_VALUE : fmtNum(point.spi3, 2)}
                        </td>
                        <td>
                          {cls?.color ? (
                            <span className="status-dot" style={{ background: cls.color }} />
                          ) : null}
                          {cls?.label ?? t('analysis.spi.noValue')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ResultBox>

            {result.current ? (
              <div
                className="note"
                data-role="current"
                style={{ borderColor: result.current.color, color: 'var(--ink)' }}
              >
                <b>
                  {t('analysis.spi.status', {
                    year: result.current.year,
                    label: result.current.label,
                  })}
                </b>{' '}
                ({t('analysis.spi.value', { value: fmtNum(result.current.value, 2) })}).{' '}
                {t('analysis.spi.refresh')}
              </div>
            ) : (
              <ResultNote>{t('analysis.spi.noCurrent')}</ResultNote>
            )}
            <ResultNote>{t('analysis.spi.scale', { months: result.scale_months })}</ResultNote>
          </>
        ) : null
      }
    >
      <label className="fld" htmlFor="drUnit">
        {t('analysis.spi.geography')}
      </label>
      <select className="std" id="drUnit" value={current} onChange={(e) => setUnitId(e.target.value)}>
        {options.map((u) => (
          <option key={u.id} value={u.id}>
            {u.name}
            {u.admin_level === 0 ? '' : ` (${u.country ?? ''})`}
          </option>
        ))}
        {options.length === 0 ? <option value="">{t('analysis.spi.noUnitOption')}</option> : null}
      </select>

      <button
        type="button"
        className="btn primary ad-run"
        id="drRun"
        disabled={run.phase === 'running'}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('analysis.spi.run')}
      </button>
    </ToolFrame>
  );
}

export default Spi;
